
import React from "react";
import { useTranslation } from "react-i18next";
import SocialShare from "./SocialShare";
import { format } from "date-fns";
import { nb, enUS } from "date-fns/locale";
import TextToSpeech from "../InterviewExpanded/TextToSpeech";
import { useIsMobile } from "@/hooks/use-mobile";


interface InterviewHeaderProps {
  timestamp: string;
  answer: string;
  aiSummary: string[] | string;
  aiResponse: string;
  articleNumber: number;
  wordCount?: number;
  isLiked: boolean;
  isBookmarked: boolean;
  context: string;
  question: string;
  intervieweeName: string;
  title: string;
  company: string;
  readingTime?: number;
  audioDuration?: number; // Add audio duration prop
  id: string;
}

const InterviewHeader: React.FC<InterviewHeaderProps> = ({
  timestamp,
  answer,
  aiSummary,
  aiResponse,
  articleNumber,
  wordCount,
  isLiked,
  isBookmarked,
  context,
  question,
  intervieweeName,
  title,
  company,
  readingTime,
  audioDuration,
  id
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  
  // Use appropriate locale for date formatting
  const dateLocale = i18n.language === 'en' ? enUS : nb;
  const formattedDate = format(new Date(timestamp), 'dd.MM.yyyy', { locale: dateLocale });

  // Audio info component that can be reused
  const audioInfo = readingTime && (
    <div className={`text-sm text-muted-foreground inline-flex items-center border border-muted rounded-full px-1 py-0.5 bg-muted ${isMobile ? '' : 'ml-3'}`}>
      <span>{audioDuration && audioDuration > 0 ? audioDuration : readingTime * 60} {t('article.seconds')}. Lytt</span>
      <span className="ml-1">
        <TextToSpeech
          text=""
          context={context}
          question={question}
          answer={answer}
          intervieweeName={intervieweeName}
          title={title}
          company={company}
          aiSummary={aiSummary?.toString()}
          aiResponse={aiResponse}
        />
      </span>
    </div>
  );

  if (isMobile) {
    return (
      <div className="mb-4 space-y-2">
        {/* First row: Date and Social Share */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <span>{formattedDate}</span>
          </div>
          
          <SocialShare 
            isLiked={isLiked}
            isBookmarked={isBookmarked}
            articleNumber={articleNumber}
            context={context}
            question={question}
            answer={answer}
            intervieweeName={intervieweeName}
            title={title}
            company={company}
            interviewId={id}
          />
        </div>
        
        {/* Second row: Audio info */}
        {audioInfo}
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div className="flex justify-between items-center mb-4">
      <div className="text-sm text-gray-500 flex items-center">
        <span>{formattedDate}</span>
        {audioInfo}
      </div>
      
      <SocialShare 
        isLiked={isLiked}
        isBookmarked={isBookmarked}
        articleNumber={articleNumber}
        context={context}
        question={question}
        answer={answer}
        intervieweeName={intervieweeName}
        title={title}
        company={company}
        interviewId={id}
      />
    </div>
  );
};

export default InterviewHeader;
